/* eslint-disable max-lines */
import { inject, Injectable } from '@angular/core';
import { each } from 'lodash-es';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { TimelineQuery } from '@models/milestone-category/milestone/timeline.query';

import {
  ActivityType,
  CategoryType,
  listTimelineMilestonesQuery,
} from '@shared/services/gql.service';

import { ForecastTableGridCategoryToggleService } from './forecast-table-grid-category-toggle.service';
import { ForecastTableGridDriverService } from './forecast-table-grid-driver.service';
import { ForecastTableGridMethodService } from './forecast-table-grid-method.service';
import { ForecastTableGridPeriodService } from './forecast-table-grid-period.service';
import { ForecastTableGridRemainingService } from './forecast-table-grid-remaining.service';
import { ForecastTableGridTotalService } from './forecast-table-grid-total.service';
import { ForecastTableGridUnitsService } from './forecast-table-grid-units.service';
import { ForecastTableGridUomService } from './forecast-table-grid-uom.service';
import { ActivityQuery } from '../../../state/activity/activity.query';
import { CategoryQuery, FullActivity, FullCategory } from '../../../state/category/category.query';
import { ForecastSettingsQuery } from '../../../state/settings/forecast-settings.query';
import { ForecastSettingsModel } from '../../../state/settings/forecast-settings.store';
import {
  ForecastTableGridCategoryTypes,
  ForecastTableGridDataInterface,
  ForecastTableGridFullCategoryExtended,
  ForecastTableGridToggleInterface,
} from '../models/forecast-table-grid.model';

@Injectable({
  providedIn: 'root',
})
export class ForecastTableGridCategoryService {
  private activityQuery = inject(ActivityQuery);
  private categoryQuery = inject(CategoryQuery);
  private driverService = inject(ForecastTableGridDriverService);
  private forecastSettingsQuery = inject(ForecastSettingsQuery);
  private methodService = inject(ForecastTableGridMethodService);
  private periodService = inject(ForecastTableGridPeriodService);
  private remainingService = inject(ForecastTableGridRemainingService);
  private timelineQuery = inject(TimelineQuery);
  private toggleService = inject(ForecastTableGridCategoryToggleService);
  private totalService = inject(ForecastTableGridTotalService);
  private unitsService = inject(ForecastTableGridUnitsService);
  private uomService = inject(ForecastTableGridUomService);

  // eslint-disable-next-line max-lines-per-function
  filterCategoryView([serviceCategories, costCategories, toggleStatus]: [
    ForecastTableGridDataInterface[],
    ForecastTableGridDataInterface[],
    ForecastTableGridToggleInterface,
  ]): [ForecastTableGridDataInterface[], ForecastTableGridDataInterface[]] {
    const toggleStatusKeys = Object.keys(toggleStatus);

    let categoryTree: ForecastTableGridFullCategoryExtended[] = [];

    let categoryPaths: Record<string, string[]> = {};
    let fullPaths: Record<string, string[]> = {};
    let toggleOffCategoryIds: string[] = [];

    const categoryMap = (incomingCategory: ForecastTableGridDataInterface) => {
      const category = incomingCategory;

      category.isOpen = category.id in toggleStatus ? toggleStatus[category.id] : false;

      // Create category paths
      if (toggleStatusKeys.includes(category.id)) {
        categoryPaths[category.id] = this.toggleService.findCategoryChildren(
          category.id,
          categoryTree
        );
      }

      // Display top-level categories
      if (!category.parentId) {
        category.isDisplayed = true;

        return category;
      }

      // Display categories whose parentId is open
      if (toggleStatusKeys.includes(category.parentId) && toggleStatus[category.parentId]) {
        category.isDisplayed = true;

        return category;
      }

      // Hide (initially) categories whose parentId is closed
      if (toggleStatusKeys.includes(category.parentId) && !toggleStatus[category.parentId]) {
        category.isDisplayed = false;

        return category;
      }

      category.isDisplayed = false;

      return category;
    };

    const createFullPaths = () => {
      // Create full paths
      each(categoryPaths, (paths, categoryId) => {
        const loopPaths = (recursivePaths: string[]) => {
          recursivePaths.forEach((path) => {
            if (categoryPaths[path]) {
              fullPaths[categoryId].push(...categoryPaths[path]);
              loopPaths(categoryPaths[path]);
            }
          });
        };

        // Initialize full path
        fullPaths[categoryId] = [...paths];

        // Recurse initial full path
        paths.forEach((path) => {
          if (categoryPaths[path]) {
            fullPaths[categoryId].push(...categoryPaths[path]);
            loopPaths(categoryPaths[path]);
          }
        });
      });

      // Remove any paths that aren't toggled off
      each(toggleStatus, (status, categoryId) => {
        if (status) {
          delete fullPaths[categoryId];
        }
      });

      // Join toggle off ids
      each(fullPaths, (paths) => {
        toggleOffCategoryIds = [...toggleOffCategoryIds, ...paths];
      });
    };

    // Toggle final paths
    const toggleMap = (incomingCategory: ForecastTableGridDataInterface) => {
      const category = incomingCategory;

      if (toggleOffCategoryIds.includes(category.id)) {
        category.isDisplayed = false;
      }

      return category;
    };

    categoryTree = this.toggleService.serviceCategoryTree;
    categoryPaths = {};
    fullPaths = {};
    toggleOffCategoryIds = [];

    const serviceCategoriesMapped = serviceCategories.map(categoryMap);

    createFullPaths();
    const serviceCategoriesToggleMapped = serviceCategoriesMapped.map(toggleMap);
    const serviceCategoriesFiltered = serviceCategoriesToggleMapped.filter(
      (category) => category.isDisplayed
    );

    categoryTree = this.toggleService.costCategoryTree;
    categoryPaths = {};
    fullPaths = {};
    toggleOffCategoryIds = [];

    const costCategoriesMapped = costCategories.map(categoryMap);

    createFullPaths();
    const costCategoriesToggleMapped = costCategoriesMapped.map(toggleMap);
    const costCategoriesFiltered = costCategoriesToggleMapped.filter(
      (category) => category.isDisplayed
    );

    return [serviceCategoriesFiltered, costCategoriesFiltered];
  }

  filterCostCategories(costCategories: FullCategory[]): FullCategory[] {
    return costCategories.filter((costCategory) => {
      return (
        costCategory.category_type === 'CATEGORY_PASSTHROUGH' ||
        costCategory.category_type === 'CATEGORY_INVESTIGATOR'
      );
    });
  }

  filterServiceCategories(serviceCategories: FullCategory[]): FullCategory[] {
    return serviceCategories.filter((serviceCategory) => {
      return (
        serviceCategory.category_type !== 'CATEGORY_PASSTHROUGH' &&
        serviceCategory.category_type !== 'CATEGORY_INVESTIGATOR' &&
        serviceCategory.category_type !== 'CATEGORY_DISCOUNT'
      );
    });
  }

  // eslint-disable-next-line max-lines-per-function
  getActivities(): Observable<ForecastTableGridDataInterface[]> {
    return combineLatest([
      this.activityQuery.selectAll({
        filterBy: (a) => {
          return a.category_id === '';
        },
      }),
      this.forecastSettingsQuery.selectAll({ asObject: true }),
      this.timelineQuery.select('items'),
      this.categoryQuery.filters$,
    ]).pipe(
      map(([activities, settings, timelineMilestones, filters]) => {
        return activities
          .map((activity) => {
            const act_primary_settings = settings[activity.primary_settings_id];
            const act_secondary_settings =
              settings[activity.secondary_settings_id] || ({} as ForecastSettingsModel);
            const mils = timelineMilestones.reduce(
              (acc, val) => {
                acc[val.milestone.id] = val;

                return acc;
              },
              {} as Record<string, listTimelineMilestonesQuery>
            );

            const categories = timelineMilestones.reduce(
              (acc, val) => {
                acc[val.milestone.milestone_category_id] = {
                  ...val.milestone.milestone_category,
                  id: val.milestone.milestone_category_id,
                };

                return acc;
              },
              {} as Record<string, { id: string; name: string }>
            );

            const fullActivity: FullActivity = {
              ...activity,
              primary_settings: {
                ...act_primary_settings,
                end_milestone: act_primary_settings?.period_end_milestone_id
                  ? mils[act_primary_settings.period_end_milestone_id]?.milestone
                  : null,
                milestone_category: act_primary_settings?.milestone_category
                  ? categories[act_primary_settings.milestone_category]
                  : null,
                start_milestone: act_primary_settings?.period_start_milestone_id
                  ? mils[act_primary_settings.period_start_milestone_id]?.milestone
                  : null,
              },
              secondary_settings: {
                ...act_secondary_settings,
                end_milestone: act_secondary_settings?.period_end_milestone_id
                  ? mils[act_secondary_settings.period_end_milestone_id]?.milestone
                  : null,
                milestone_category: act_secondary_settings?.milestone_category
                  ? categories[act_secondary_settings.milestone_category]
                  : null,
                start_milestone: act_secondary_settings?.period_start_milestone_id
                  ? mils[act_secondary_settings.period_start_milestone_id]?.milestone
                  : null,
              },
            };

            return this.parseCategory({
              category: fullActivity,
              categoryIndex: 0,
              fullActivity,
              fullCategory: null,
              fullParentCategory: null,
              indentLevel: 0,
              isChild: true,
              isCost: false,
              parentCategory: null,
              parentId: '',
            });
          })
          .filter((data) => {
            if (data.fullActivity) {
              return this.categoryQuery.filterCatOrAct(data.fullActivity.primary_settings, filters);
            }

            return false;
          });
      })
    );
  }

  getCostCategories(): Observable<ForecastTableGridDataInterface[]> {
    return this.categoryQuery.selectTopCategories$.pipe(
      map(this.filterCostCategories.bind(this)),
      map(this.parseCostCategories.bind(this))
    );
  }

  getServiceCategories(): Observable<ForecastTableGridDataInterface[]> {
    return this.categoryQuery.selectTopCategories$.pipe(
      map(this.filterServiceCategories.bind(this)),
      map(this.parseServiceCategories.bind(this))
    );
  }

  parseActivityCheckedCount(category: FullActivity | FullCategory): number {
    return 'activity_checked_count' in category ? category.activity_checked_count || 0 : 0;
  }

  // eslint-disable-next-line max-lines-per-function
  parseCategory({
    category,
    categoryIndex,
    fullActivity,
    fullCategory,
    fullParentCategory,
    indentLevel = 0,
    isChild,
    isCost,
    parentCategory,
    parentId,
  }: {
    category: FullActivity | FullCategory;
    categoryIndex: number;
    fullActivity: FullActivity | null;
    fullCategory: FullCategory | null;
    fullParentCategory: FullCategory | null;
    indentLevel: null | number;
    isChild: boolean;
    isCost: boolean;
    parentCategory: ForecastTableGridDataInterface | null;
    parentId: null | string;
  }): ForecastTableGridDataInterface {
    let costCategoryType = '';

    if ('category_type' in category && category.category_type) {
      switch (category.category_type) {
        case CategoryType.CATEGORY_DISCOUNT:
          costCategoryType = 'Discount';
          break;
        case CategoryType.CATEGORY_INVESTIGATOR:
          costCategoryType = 'Investigator';
          break;
        case CategoryType.CATEGORY_PASSTHROUGH:
          costCategoryType = 'Pass-through';
          break;
        case CategoryType.CATEGORY_ROOT:
          break;

        case CategoryType.CATEGORY_SERVICE:
          costCategoryType = 'Services';
          break;
        case CategoryType.CATEGORY_UNDETERMINED:
          break;
      }
    }

    if ('activity_type' in category && category.activity_type) {
      switch (category.activity_type) {
        case ActivityType.ACTIVITY_DISCOUNT:
          costCategoryType = 'Discount';
          break;
        case ActivityType.ACTIVITY_INVESTIGATOR:
          costCategoryType = 'Investigator';
          break;
        case ActivityType.ACTIVITY_PASSTHROUGH:
          costCategoryType = 'Pass-through';
          break;
        case ActivityType.ACTIVITY_SERVICE:
          costCategoryType = 'Services';
          break;
      }
    }

    return {
      activityCheckedCount: this.parseActivityCheckedCount(category),
      categoryIndex,
      categoryName: this.parseCategoryName(category),
      costCategoryType,
      driver: this.driverService.parseCategoryDriver(category),
      driverSettingId: this.driverService.parseCategoryDriverSettingId(category),
      fullActivity,
      fullCategory,
      fullParentCategory,
      id: category.id,
      indentLevel: indentLevel || 0,
      isChild,
      isCost,
      isDisplayed: false,
      isOpen: false,
      method: this.methodService.parseCategoryMethod(category),
      parentCategory,
      parentId,
      period: this.periodService.parseCategoryPeriod(category),
      primarySettingsId: this.parsePrimarySettingsId(category),
      primarySettingsOverride: this.parsePrimarySettingsOverride(category),
      remaining: this.remainingService.parseCategoryRemaining(category),
      subCategory: this.parseIsSubCategory(category, isChild),
      total: this.totalService.parseCategoryTotal(category),
      units: this.unitsService.parseCategoryUnits(category, isChild),
      uom: this.uomService.parseCategoryUom(category, isChild),
    };
  }

  parseCategoryName(category: FullActivity | FullCategory): string {
    return `${category.display_label} ${category.name}`;
  }

  parseCostCategories(categories: FullCategory[]): ForecastTableGridDataInterface[] {
    let parsedCostCategories: ForecastTableGridDataInterface[] = [];
    const categoryTree = this.processSubCategories(categories, ForecastTableGridCategoryTypes.Cost);

    categoryTree.forEach((category, categoryIndex) => {
      // Parse category
      const parsedCategory = this.parseCategory({
        category,
        categoryIndex,
        fullActivity: null,
        fullCategory: category,
        fullParentCategory: null,
        indentLevel: null,
        isChild: false,
        isCost: true,
        parentCategory: null,
        parentId: null,
      });

      parsedCostCategories.push(parsedCategory);

      // Parse activities
      category.activities.forEach((activity) => {
        const parsedActivity = this.parseCategory({
          category: activity,
          categoryIndex,
          fullActivity: activity,
          fullCategory: null,
          fullParentCategory: category,
          indentLevel: 1,
          isChild: true,
          isCost: true,
          parentCategory: parsedCategory,
          parentId: parsedCategory.id,
        });

        parsedCostCategories.push(parsedActivity);
      });

      // Parse sub categories
      if (category.subCategories.length) {
        const parsedSubCategories = this.parseSubCategories(
          category.subCategories,
          categoryIndex,
          true,
          parsedCategory,
          category
        );

        parsedCostCategories = [...parsedCostCategories, ...parsedSubCategories];
      }
    });

    return parsedCostCategories;
  }

  parseIsSubCategory(category: FullActivity | FullCategory, isChild: boolean): boolean {
    return Boolean(isChild && 'parent_category_id' in category && category.parent_category_id);
  }

  parsePrimarySettingsId(category: FullActivity | FullCategory): string {
    return category.primary_settings.id;
  }

  parsePrimarySettingsOverride(category: FullActivity | FullCategory): boolean {
    return category.primary_settings.override;
  }

  parseServiceCategories(categories: FullCategory[]): ForecastTableGridDataInterface[] {
    let parsedServiceCategories: ForecastTableGridDataInterface[] = [];
    const categoryTree = this.processSubCategories(
      categories,
      ForecastTableGridCategoryTypes.Service
    );

    categoryTree.forEach((category, categoryIndex) => {
      // Parse category
      const parsedCategory = this.parseCategory({
        category,
        categoryIndex,
        fullActivity: null,
        fullCategory: category,
        fullParentCategory: null,
        indentLevel: 0,
        isChild: false,
        isCost: false,
        parentCategory: null,
        parentId: null,
      });

      parsedServiceCategories.push(parsedCategory);

      // Parse activities
      category.activities.forEach((activity) => {
        const parsedActivity = this.parseCategory({
          category: activity,
          categoryIndex,
          fullActivity: activity,
          fullCategory: null,
          fullParentCategory: category,
          indentLevel: 1,
          isChild: true,
          isCost: false,
          parentCategory: parsedCategory,
          parentId: parsedCategory.id,
        });

        parsedServiceCategories.push(parsedActivity);
      });

      // Parse sub categories
      if (category.subCategories.length) {
        const parsedSubCategories = this.parseSubCategories(
          category.subCategories,
          categoryIndex,
          false,
          parsedCategory,
          category
        );

        parsedServiceCategories = [...parsedServiceCategories, ...parsedSubCategories];
      }
    });

    return parsedServiceCategories;
  }

  parseSubCategories(
    subCategories: ForecastTableGridFullCategoryExtended[],
    categoryIndex: number,
    isCost: boolean,
    initialParentCategory: ForecastTableGridDataInterface,
    initialFullParentCategory: FullCategory
  ): ForecastTableGridDataInterface[] {
    const parsedCategories: ForecastTableGridDataInterface[] = [];

    const recurseSubCategories = (
      fullCategory: ForecastTableGridFullCategoryExtended,
      parentCategory: ForecastTableGridDataInterface,
      fullParentCategory: FullCategory,
      recurseLevel: number
    ) => {
      const parsedSubCategory = this.parseCategory({
        category: fullCategory,
        categoryIndex,
        fullActivity: null,
        fullCategory,
        fullParentCategory,
        indentLevel: recurseLevel,
        isChild: true,
        isCost,
        parentCategory,
        parentId: parentCategory.id,
      });

      parsedCategories.push(parsedSubCategory);

      fullCategory.activities.forEach((subCategoryActivity) => {
        const parsedActivity = this.parseCategory({
          category: subCategoryActivity,
          categoryIndex,
          fullActivity: subCategoryActivity,
          fullCategory: null,
          fullParentCategory: fullCategory,
          indentLevel: recurseLevel + 1,
          isChild: true,
          isCost,
          parentCategory: parsedSubCategory,
          parentId: parsedSubCategory.id,
        });

        parsedCategories.push(parsedActivity);
      });

      if (fullCategory.subCategories.length) {
        fullCategory.subCategories.forEach((subCategory) => {
          recurseSubCategories(subCategory, parsedSubCategory, fullCategory, recurseLevel + 1);
        });
      }
    };

    subCategories.forEach((subCategory) => {
      recurseSubCategories(subCategory, initialParentCategory, initialFullParentCategory, 1);
    });

    return parsedCategories;
  }

  processSubCategories(
    categories: FullCategory[],
    categoryType: ForecastTableGridCategoryTypes
  ): ForecastTableGridFullCategoryExtended[] {
    // Initially, subcategories are listed on the first level
    // of the category array. Because they should be in tree format,
    // this array will store the subcategories that should
    // be removed from the first level after processing
    let subCategoryIdsToDelete: string[] = [];
    let categoryTree: ForecastTableGridFullCategoryExtended[] = [];
    const categoryMap: Record<string, ForecastTableGridFullCategoryExtended> = {};

    categories.forEach((category) => {
      categoryMap[category.id] = { ...category, subCategories: [] };
      subCategoryIdsToDelete = [...subCategoryIdsToDelete, ...category.sub_category_ids];
    });

    categories.forEach((category) => {
      if (category.parent_category_id) {
        categoryMap[category.parent_category_id].subCategories.push(categoryMap[category.id]);
      } else {
        categoryTree.push(categoryMap[category.id]);
      }
    });

    categoryTree = categoryTree.filter((category) => !subCategoryIdsToDelete.includes(category.id));

    if (categoryType === ForecastTableGridCategoryTypes.Service) {
      this.toggleService.serviceCategoryTree = categoryTree;
    } else {
      this.toggleService.costCategoryTree = categoryTree;
    }

    return categoryTree;
  }
}
